/** @format */
import React, { Fragment, ReactNode } from 'react';

import { AvailableMarket, MarketCode } from '@toucan/client/global/market';

import { UrlFactory } from '@toucan/client/features/seo';

type GetHrefLangElementsProps = {
    factory: UrlFactory;
    xDefaultMarketCode?: MarketCode;
    availableMarkets: AvailableMarket[];
};

type HreflangMapping = [string, MarketCode];

export const getMappings = (
    availableMarkets: AvailableMarket[]
): HreflangMapping[] => {
    /**
     * If the MarketCode is found in availableMarkets, then use the MarketCode,
     * otherwise use the default as a fallback
     */
    const marketsWithFallbacks = availableMarkets.reduce(
        (markets, market) => {
            // @ts-ignore - Element implicitly has an any type - Touch and Change
            if (markets[market.code]) {
                return { ...markets, [market.code]: market.code };
            }

            return markets;
        },
        {
            [MarketCode.EN_IE]: MarketCode.EN_EU,
            [MarketCode.DE_AT]: MarketCode.DE_DE,
            [MarketCode.FR_BE]: MarketCode.FR_FR,
            [MarketCode.NL_BE]: MarketCode.NL_NL,
        }
    );

    return [
        ['en-GB', MarketCode.EN_UK],
        ['en-US', MarketCode.EN_US],
        ['en-IE', marketsWithFallbacks[MarketCode.EN_IE]],
        ['en-BE', MarketCode.EN_EU],
        ['en-MT', MarketCode.EN_EU],
        ['en', MarketCode.EN_EU],
        ['de', MarketCode.DE_DE],
        ['de-AT', marketsWithFallbacks[MarketCode.DE_AT]],
        ['de-BE', MarketCode.DE_DE],
        ['de-DE', MarketCode.DE_DE],
        ['de-LI', MarketCode.DE_DE],
        ['de-LU', MarketCode.DE_DE],
        ['de-CH', MarketCode.DE_DE],
        ['fr', MarketCode.FR_FR],
        ['fr-BE', marketsWithFallbacks[MarketCode.FR_BE]],
        ['fr-FR', MarketCode.FR_FR],
        ['fr-LU', MarketCode.FR_FR],
        ['fr-MC', MarketCode.FR_FR],
        ['fr-CH', MarketCode.FR_FR],
        ['nl', MarketCode.NL_NL],
        ['nl-NL', MarketCode.NL_NL],
        ['nl-BE', marketsWithFallbacks[MarketCode.NL_BE]],
        ['it-IT', MarketCode.IT_IT],
        ['es-ES', MarketCode.ES_ES],
        ['pt-PT', MarketCode.PT_PT],
    ];
};

export const getHrefLangElements = ({
    factory,
    xDefaultMarketCode = MarketCode.EN_US,
    availableMarkets,
}: GetHrefLangElementsProps): ReactNode => {
    function getLinks(): ReactNode {
        const urls = factory.getUrls(availableMarkets);
        const xDefaultMarket = availableMarkets.find(
            (market) => market.code === xDefaultMarketCode
        );

        return (
            <Fragment>
                {getMappings(availableMarkets).map(([language, marketCode]) =>
                    urls[marketCode] ? (
                        <link
                            key={`hreflang__${language}`}
                            rel="alternate"
                            hrefLang={language}
                            href={urls[marketCode]}
                        />
                    ) : null
                )}
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {xDefaultMarket && urls[xDefaultMarket.code] && (
                    <link
                        rel="alternate"
                        hrefLang="x-default"
                        href={urls[xDefaultMarket.code]}
                    />
                )}
            </Fragment>
        );
    }

    return getLinks();
};
